// File: src/components/common/Input.tsx
// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// version = "1.2.0"
//
// Changelog:
//
// Version 1.2.0:
// - [Fixed] Corrected the component to properly merge externally provided
//   `className` props with its internal base classes, resolving a style
//   override bug that caused layout issues.
//
// Version 1.1.0:
// - [Fixed] Conditionally applies a CSS class to hide default browser spinners on
//   number inputs, resolving a UI overlap issue.
// ==============================================================================
import React, { InputHTMLAttributes } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { useDirection } from '../../hooks/useDirection';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  wrapperClassName?: string;
}

const Input: React.FC<InputProps> = ({ label, name, error, wrapperClassName = "mb-4", className = '', ...props }) => {
  const { isRTL, direction } = useDirection();
  const isDateType = props.type === 'date' || props.type === 'datetime-local';
  const isNumberType = props.type === 'number';

  // Base classes for all inputs with RTL support
  const baseClasses = `mt-1 block w-full py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-sm`;
  const errorClass = error ? 'border-red-500' : 'border-neutral-DEFAULT';
  const rtlClasses = isRTL ? 'text-right' : 'text-left';
  
  // Apply specific padding and custom classes to hide native icons
  let typeSpecificClasses = 'px-3';
  if (isDateType) {
    typeSpecificClasses = isRTL ? 'hide-date-icon pr-3 pl-10' : 'hide-date-icon pl-3 pr-10';
  } else if (isNumberType) {
    typeSpecificClasses = 'hide-number-spinners px-3';
  }

  return (
    <div className={wrapperClassName}>
      {label && (
        <label htmlFor={name} className={`block text-sm font-medium text-neutral-dark mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
          {label}
        </label>
      )}
      <div className="relative rounded-md shadow-sm">
        <input
          id={name}
          name={name}
          dir={direction}
          className={`${baseClasses} ${errorClass} ${typeSpecificClasses} ${rtlClasses} ${className}`}
          {...props}
        />
        {isDateType && (
          <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
            <CalendarIcon className="h-5 w-5 text-neutral-400" />
          </div>
        )}
      </div>
      {error && <p className={`mt-1 text-xs text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>{error}</p>}
    </div>
  );
};

export default Input;